/**
 * Central API helper — all fetch calls to the PHP backend go through here.
 * Automatically attaches the JWT Bearer token from localStorage.
 */

function resolveBaseUrl() {
  const env = process.env.REACT_APP_API_URL || '/backend/api';
  if (typeof window === 'undefined') return env;
  // Phone / LAN: localhost in .env points at the phone itself — use PC hostname instead
  const host = window.location.hostname;
  if (host && host !== 'localhost' && host !== '127.0.0.1' && /localhost|127\.0\.0\.1/.test(env)) {
    return env.replace(/localhost|127\.0\.0\.1/g, host);
  }
  return env;
}

const BASE_URL = resolveBaseUrl();

function getToken() {
  return localStorage.getItem('admin_token') || '';
}

async function request(endpoint, options = {}) {
  const token = getToken();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  let res;
  try {
    res = await fetch(`${BASE_URL}/${endpoint}`, {
      ...options,
      headers,
    });
  } catch (err) {
    // Common on localhost: PHP API exists only on Hostinger after upload
    const isLocal = typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname);
    throw new Error(
      isLocal
        ? 'API not available on localhost. Upload the site + backend/ folder to Hostinger, then login works.'
        : 'Cannot reach API. Check that backend/ is uploaded and config.php MySQL settings are correct.'
    );
  }

  const json = await res.json().catch(() => ({ success: false, message: 'Invalid server response' }));

  if (!res.ok && res.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/admin') && !path.startsWith('/admin/login')) {
      window.location.href = '/admin/login';
    }
  }

  return json;
}

// ── Auth ───────────────────────────────────────────────────
export const authApi = {
  login: (username, password) =>
    request('auth/login.php', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
};

// ── Events ─────────────────────────────────────────────────
export const eventsApi = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''))
    ).toString();
    return request(`events.php${qs ? '?' + qs : ''}`);
  },
  create: (data) => request('events.php', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`events.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`events.php?id=${id}`, { method: 'DELETE' }),
};

// ── Gallery ────────────────────────────────────────────────
export const galleryApi = {
  getAll: (category = '') =>
    request(`gallery.php${category && category !== 'all' ? '?category=' + category : ''}`),
  create: (formData) =>
    request('gallery.php', {
      method: 'POST',
      headers: {},                // Let browser set multipart boundary
      body: formData,
    }),
  createJson: (data) =>
    request('gallery.php', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) =>
    request(`gallery.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`gallery.php?id=${id}`, { method: 'DELETE' }),
};

// ── Katha ──────────────────────────────────────────────────
export const kathaApi = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''))
    ).toString();
    return request(`katha.php${qs ? '?' + qs : ''}`);
  },
  create: (data) => request('katha.php', { method: 'POST', body: JSON.stringify(data) }),
  createFormData: (formData) => request('katha.php', { method: 'POST', headers: {}, body: formData }),
  update: (id, data) => request(`katha.php?id=${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateFormData: (id, formData) => request(`katha.php?id=${id}`, { method: 'PUT', headers: {}, body: formData }),
  delete: (id) => request(`katha.php?id=${id}`, { method: 'DELETE' }),
};

// ── Contacts ───────────────────────────────────────────────
export const contactsApi = {
  submit: (data) => request('contacts.php', { method: 'POST', body: JSON.stringify(data) }),
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''))
    ).toString();
    return request(`contacts.php${qs ? '?' + qs : ''}`);
  },
  updateStatus: (id, status) =>
    request(`contacts.php?id=${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),
  delete: (id) => request(`contacts.php?id=${id}`, { method: 'DELETE' }),
};

// ── Home Content ───────────────────────────────────────────
export const homeApi = {
  getAll: () => request('home.php'),
  getKey: (key) => request(`home.php?key=${key}`),
  updateKey: (key, value) =>
    request('home.php', { method: 'PUT', body: JSON.stringify({ key, value }) }),
  uploadImage: (formData) =>
    request('home.php', { method: 'POST', headers: {}, body: formData }),
};

// ── Stats (dashboard) ──────────────────────────────────────
export const statsApi = {
  get: () => request('stats.php'),
};
